export type ContractAddress = {
  street: string;
  number: string;
  complement: string;
  neighborhood: string;
  city: string;
  state: string;
  zipCode: string;
};

export const emptyContractAddress = (): ContractAddress => ({
  street: '', number: '', complement: '', neighborhood: '', city: '', state: '', zipCode: '',
});

export const formatContractAddress = (address: ContractAddress): string => {
  const firstLine = [address.street.trim(), address.number.trim(), address.complement.trim()].filter(Boolean).join(', ');
  const secondLine = [address.city.trim(), address.state.trim().toUpperCase()].filter(Boolean).join(' - ');
  return [
    [firstLine, address.neighborhood.trim()].filter(Boolean).join(' - '),
    [secondLine, address.zipCode.trim()].filter(Boolean).join(', '),
  ].filter(Boolean).join('\n');
};

// A segunda linha é "cidade - UF, CEP", mas `formatContractAddress` descarta
// os separadores dos campos vazios — então UF em branco produz "Recife, CEP",
// sem o " - " que dividia cidade e UF. Ler por posição devolvia a linha
// inteira como cidade e PERDIA o CEP a cada leitura.
//
// Por isso lê de trás para a frente, pelo formato de cada pedaço: o CEP é o
// único com forma própria (5+3 dígitos no fim da linha) e a UF são duas letras
// depois de " - ". O que sobra é a cidade — que é o único campo de texto livre
// aqui, e o que pode conter vírgula ou hífen sem espaços ("Mogi-Guaçu").
const CEP_NO_FIM = /(?:^|,)\s*(\d{5}-?\d{3})\s*$/;
const UF_NO_FIM = /\s+-\s+([A-Za-z]{2})\s*$/;

export const parseContractAddress = (value = ''): ContractAddress => {
  const [firstLine = '', secondLine = ''] = value.split('\n');
  const [streetAndNumber = '', neighborhood = ''] = firstLine.split(/\s+-\s+/, 2);

  // "rua, número, complemento": a primeira linha é lida da FRENTE, pelas duas
  // primeiras vírgulas. O complemento é o único campo de texto livre daqui e
  // carrega vírgula com naturalidade ("Apto 302, Bloco B"), então tudo o que
  // vem depois da segunda vírgula é dele. O preço é o inverso: nome de rua
  // com vírgula passaria a virar número — o que não acontece na prática.
  const firstComma = streetAndNumber.indexOf(',');
  const street = (firstComma < 0 ? streetAndNumber : streetAndNumber.slice(0, firstComma)).trim();
  const afterStreet = firstComma < 0 ? '' : streetAndNumber.slice(firstComma + 1);
  const secondComma = afterStreet.indexOf(',');
  const number = (secondComma < 0 ? afterStreet : afterStreet.slice(0, secondComma)).trim();
  // Complemento sem número volta do parse como número — mesma perda que a UF
  // em branco já tinha. Não incomoda porque quem edita mantém estado próprio
  // e o número é obrigatório nas duas telas que preenchem o endereço.
  const complement = secondComma < 0 ? '' : afterStreet.slice(secondComma + 1).trim();

  const cep = secondLine.match(CEP_NO_FIM);
  const zipCode = cep ? cep[1] : '';
  const cityAndState = (cep ? secondLine.slice(0, cep.index) : secondLine).trim();
  const uf = cityAndState.match(UF_NO_FIM);
  const state = uf ? uf[1].toUpperCase() : '';
  const city = (uf ? cityAndState.slice(0, uf.index) : cityAndState).trim();

  return { street, number, complement, neighborhood, city, state, zipCode };
};

export const formatZipCode = (value: string): string => {
  const digits = value.replace(/\D/g, '').slice(0, 8);
  return digits.replace(/^(\d{5})(\d)/, '$1-$2');
};
